import type { IUserDetails } from '../../storage/store';
import type { IGenericCta } from '../../interfaces/Withdrawal';
import { NAVIGATION_ROUTES } from '../../constant';

/**
 * Stand-in for the balance before login. A real ₹0 would read as "you have
 * nothing", when in fact we simply do not know yet.
 */
export const MASKED_BALANCE = '₹xxx';

export interface IWithdrawEligibility {
  /** Withdrawable winnings, in whole rupees. */
  balance: number;
  /** Server-configured minimum, when it is present in the user details. */
  minWithdrawal?: number;
  isEligible: boolean;
}

/**
 * Testing only: opens the withdrawal CTA to a ₹0 account.
 *
 * KYC is only reachable through the product by tapping Withdraw, which a test
 * account with no winnings can never do — so the CTA → gate → KYC →
 * `resumeWithdraw` path cannot be exercised without real money in the account.
 *
 * A flag rather than a relaxed comparison, so it cannot be forgotten in a diff
 * and shipped. Off by default: an unset variable leaves eligibility exactly as
 * it was. Never set this in `.env.production` — at ₹0 every Withdraw tap would
 * reach KYC and spend a billed SurePass `initialize` on a user who still cannot
 * withdraw.
 */
export const isBalanceGateSkipped = (): boolean =>
  (process.env.REACT_APP_SKIP_BALANCE_GATE || '').trim() === 'true';

/**
 * Decides whether the user can start a withdrawal.
 *
 * `actualCoins` is the withdrawable winnings balance; `minWithdrawal` is the
 * server's floor. Both come from `getUserDetails` via `useUserStore`.
 *
 * `minWithdrawal` is typed on `IUserDetails` but has not been confirmed against
 * a live response, so its absence falls back to "any positive balance" rather
 * than locking the user out of a feature they are entitled to.
 */
export function getWithdrawEligibility(
  userDetails?: IUserDetails,
): IWithdrawEligibility {
  const balance = Math.floor(userDetails?.actualCoins ?? 0);
  const minWithdrawal = userDetails?.minWithdrawal;

  // Only the client-side route opens up. `balance` and `minWithdrawal` are
  // reported untouched so the screen still shows the real figures, and the
  // withdrawal itself is still refused server-side.
  const isEligible =
    isBalanceGateSkipped() ||
    (typeof minWithdrawal === 'number' && minWithdrawal > 0
      ? balance >= minWithdrawal
      : balance > 0);

  return { balance, minWithdrawal, isEligible };
}

/**
 * Routes a server-driven `IN_APP` CTA.
 *
 * Android hands these to a GenericCTAHandler with a large value table; only the
 * one value the withdrawal gate emits is mapped here. An unrecognised value
 * returns null so the caller closes the sheet rather than navigating somewhere
 * arbitrary.
 */
const IN_APP_CTA_ROUTES: Record<string, string> = {
  SAVE_WITHDRAWAL_MODE: NAVIGATION_ROUTES.WITHDRAW_MODES,
};

export function resolveInAppCta(cta?: IGenericCta): string | null {
  if (!cta || cta.type !== 'IN_APP' || !cta.value) return null;
  return IN_APP_CTA_ROUTES[cta.value] ?? null;
}
